import type { ModelCapability, ModelCatalog, ModelProvider } from "./hermes-chat-adapter";

export type ReasoningEffort = "none" | "minimal" | "low" | "medium" | "high" | "xhigh" | "max";

export type ModelSelection = {
  provider: string;
  model: string;
};

const OPENAI_COMPAT: ReasoningEffort[] = ["none", "minimal", "low", "medium", "high", "xhigh", "max"];
const CODEX_GPT56: ReasoningEffort[] = ["none", "low", "medium", "high", "xhigh", "max"];
const CODEX_LEGACY: ReasoningEffort[] = ["none", "low", "medium", "high", "xhigh"];

export function selectionValue(selection: ModelSelection) {
  return `${selection.provider}|${selection.model}`;
}

export function parseSelectionValue(value: string): ModelSelection {
  const separator = value.indexOf("|");
  return separator < 0
    ? { provider: "", model: value }
    : { provider: value.slice(0, separator), model: value.slice(separator + 1) };
}

export function selectedProvider(catalog: ModelCatalog, selection: ModelSelection): ModelProvider | undefined {
  return catalog.providers.find((provider) => provider.slug === selection.provider);
}

export function selectedCapability(catalog: ModelCatalog, selection: ModelSelection): ModelCapability | undefined {
  return selectedProvider(catalog, selection)?.capabilities[selection.model];
}

export function supportedReasoningEfforts(
  selection: ModelSelection,
  capability?: ModelCapability,
): ReasoningEffort[] {
  if (capability?.reasoning === false) return [];
  const provider = selection.provider.toLowerCase();
  const model = selection.model.toLowerCase();
  let efforts: ReasoningEffort[];

  if (provider === "openai-codex" || provider === "openai-api") {
    efforts = model.includes("gpt-5.6") ? CODEX_GPT56 : CODEX_LEGACY;
  } else if (provider === "nous" || provider === "openrouter") {
    efforts = OPENAI_COMPAT;
  } else if (model.includes("x-preview-f-free") || model.includes("ox-alpha")) {
    efforts = ["low", "high", "max"];
  } else if (/(^|\/)kimi-k3(?:[^a-z0-9]|$)|(^|\/)k3(?:[^a-z0-9]|$)/.test(model)) {
    efforts = ["low", "high", "max"];
  } else if (model.includes("glm-5.2")) {
    efforts = ["high", "max"];
  } else if (model.includes("glm-5.3") || model.includes("deepseek-v4")) {
    efforts = ["low", "medium", "high", "max"];
  } else if (model.includes("grok-4.6")) {
    efforts = ["low", "medium", "high", "xhigh"];
  } else {
    efforts = OPENAI_COMPAT;
  }

  return capability?.canDisableReasoning === false
    ? efforts.filter((effort) => effort !== "none")
    : efforts;
}

export function defaultEffort(efforts: ReasoningEffort[], preferred?: ReasoningEffort): ReasoningEffort {
  if (preferred && efforts.includes(preferred)) return preferred;
  if (efforts.includes("medium")) return "medium";
  if (efforts.includes("high")) return "high";
  return efforts[0] ?? "medium";
}
